/**
 * MAHI — the profile panel. "A room of their own, not a settings menu."
 *
 * A slide-over from the left on desktop, a bottom sheet on mobile — same
 * component, framer-motion variants picked by useIsMobile() rather than two
 * separate implementations, since everything but the entry direction and
 * shape is identical. Deeper sections (Wardrobe, Account Details, the
 * stubs) are real routes the panel navigates to and closes for, not nested
 * panels of their own.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Brain, Camera, Compass, Gift, LogOut, MessageSquareHeart,
  Settings, ShieldCheck, SlidersHorizontal, X,
} from 'lucide-react';
import mahi from '../../api';
import { KEYS, readCached, writeCached } from '../../cache';
import { useMahiAuth } from '../../auth';
import MahiRobot from '../MahiRobot';
import ProfileNavRow from './ProfileNavRow';
import {
  AlmostYoursMark, ChatHistoryMark, StyleDnaMark, WardrobeMark, WhatYouSeeMark,
} from '../ui/marks';
import useFocusTrap from '../../hooks/useFocusTrap';
import useReducedMotion from '../../hooks/useReducedMotion';
import useIsMobile from '../../hooks/useIsMobile';
import packageJson from '../../../../package.json';

const APP_VERSION = packageJson.version;

export default function ProfilePanel({ onClose, onReplayTour }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useMahiAuth();
  const panelRef = useRef(null);
  const reduced = useReducedMotion();
  const isMobile = useIsMobile();

  const [styleLine, setStyleLine] = useState('');
  // The sub-label under Wardrobe. Seeded from cache so the row reads
  // "12 pieces · 3 tried on" the instant the panel opens rather than
  // appearing a beat later and shifting the rows under the user's thumb.
  const cachedCount = useMemo(() => readCached(KEYS.wardrobeCount), []);
  const [wardrobeCount, setWardrobeCount] = useState(cachedCount?.count ?? null);
  const [triedCount, setTriedCount] = useState(cachedCount?.tried ?? 0);

  useFocusTrap(panelRef, { active: true, onEscape: onClose });

  useEffect(() => {
    let cancelled = false;
    mahi.post('/agent/profile-summary')
      .then(({ data }) => { if (!cancelled) setStyleLine((data.text || '').trim()); })
      .catch(() => {});
    mahi.get('/wardrobe/count')
      .then(({ data }) => {
        const count = data.count ?? null;
        const tried = data.tried ?? 0;
        // Cached even if this panel closed mid-flight — the next open wants it.
        writeCached(KEYS.wardrobeCount, { count, tried });
        if (cancelled) return;
        setWardrobeCount(count);
        setTriedCount(tried);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // "12 pieces · 3 tried on" — the try-on count is what the Style Studio row
  // used to be for, so it keeps its visibility without keeping its screen.
  // Dropped entirely when nothing has been tried, rather than reading "· 0".
  const wardrobeSublabel = wardrobeCount == null
    ? undefined
    : `${wardrobeCount} ${wardrobeCount === 1 ? 'piece' : 'pieces'}` +
      (triedCount > 0 ? ` · ${triedCount} tried on` : '');

  // Deliberately leaves nothing behind for the way back: back from any of
  // these screens lands on Discover, not on this panel. The profile is a
  // launcher rather than a stop on the journey, and the deck is where a user
  // should end up — reopening the panel would put one more thing between them
  // and the cards.
  const go = (path, state) => { onClose(); navigate(path, state ? { state } : undefined); };

  const firstName = (user?.name || '').trim().split(' ')[0];

  const panelShape = isMobile
    ? {
        className: 'fixed inset-x-0 bottom-0 z-50 max-h-[88vh] rounded-t-[2rem] flex flex-col',
        initial: reduced ? { opacity: 0 } : { y: '100%' },
        animate: reduced ? { opacity: 1 } : { y: 0 },
        exit: reduced ? { opacity: 0 } : { y: '100%' },
      }
    : {
        className: 'fixed inset-y-0 left-0 z-50 w-[min(90vw,24rem)] rounded-r-[1.75rem] flex flex-col',
        initial: reduced ? { opacity: 0 } : { x: '-100%' },
        animate: reduced ? { opacity: 1 } : { x: 0 },
        exit: reduced ? { opacity: 0 } : { x: '-100%' },
      };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: reduced ? 0.15 : 0.25 }}
        onClick={onClose}
        className="fixed inset-0 z-40 bg-slate-900/45 backdrop-blur-sm"
        aria-hidden="true"
      />
      <motion.div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Your profile"
        data-testid="profile-panel"
        initial={panelShape.initial}
        animate={panelShape.animate}
        exit={panelShape.exit}
        transition={reduced ? { duration: 0.18 } : { type: 'spring', stiffness: 300, damping: 32, mass: 0.9 }}
        className={`${panelShape.className} bg-white shadow-[0_30px_80px_-20px_rgba(15,23,42,0.5)]`}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="profile-panel-close"
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-50 active:scale-90 transition-transform z-10"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex-1 overflow-y-auto px-5 pt-7 pb-4">
          {/* header */}
          <button
            type="button"
            onClick={() => go('/profile/account')}
            data-testid="profile-panel-avatar"
            className="flex items-center gap-3 mb-1 active:scale-[0.98] transition-transform"
          >
            <div className="relative w-16 h-16 rounded-full bg-slate-100 ring-1 ring-slate-200 overflow-hidden flex items-center justify-center shrink-0">
              {user?.picture
                ? <img src={user.picture} alt="" className="w-full h-full object-cover" />
                : <span className="text-xl font-semibold text-slate-400 font-mahi-sans">{(user?.name || 'U').slice(0, 1).toUpperCase()}</span>}
              <span className="absolute bottom-0 inset-x-0 h-5 bg-slate-900/55 flex items-center justify-center">
                <Camera className="w-3 h-3 text-white" />
              </span>
            </div>
            <p className="font-mahi-serif text-2xl text-slate-900 text-left">{firstName || 'You'}</p>
          </button>

          {styleLine && (
            <p className="text-[13px] text-mahiteal-700 font-mahi-sans leading-snug mb-6 pl-1" data-testid="profile-style-line">
              {styleLine}
            </p>
          )}
          {!styleLine && <div className="mb-6" />}

          {/* primary nav */}
          <div className="flex flex-col gap-0.5 mb-2">
            {/* Style Studio used to be its own row here. It was the same
                pieces as the Wardrobe, split by whether they had been tried
                on — which meant looking in two places for one closet, and the
                try-on photo (the thing worth coming back for) lived in the one
                you were less likely to open. The Wardrobe now holds both and
                shows the try-on as the tile. */}
            <ProfileNavRow
              art={<WardrobeMark className="w-full h-full" />}
              label="Wardrobe"
              sublabel={wardrobeSublabel}
              onClick={() => go('/wardrobe')}
              testId="profile-nav-wardrobe"
            />
            <ProfileNavRow
              art={<AlmostYoursMark />}
              label="Almost Yours"
              sublabel="Things you almost bought"
              onClick={() => go('/almost-yours')}
              testId="profile-nav-almost-yours"
            />
            <ProfileNavRow
              art={<ChatHistoryMark />}
              label="Chat history"
              sublabel="Past conversations with MAHI"
              onClick={() => go('/chat-history')}
              testId="profile-nav-chat-history"
            />
            <ProfileNavRow
              art={<StyleDnaMark />}
              label="Style DNA"
              sublabel="What your taste says about you"
              onClick={() => go('/style-dna')}
              testId="profile-nav-style-dna"
            />
            {/* Sits with the destinations rather than down in the quiet
                settings block, because it is the one row here that changes
                what the feed actually contains — the actionable sibling of
                Style DNA above, which only reports on it. The sublabel is
                composed server-side and rides in on /auth/me
                (services/mahi/discovery_prefs.py::summary), so showing the
                current picks costs the panel no extra request. */}
            <ProfileNavRow
              art={<WhatYouSeeMark />}
              label="What you see"
              sublabel={user?.discovery_summary || 'Categories and budget'}
              onClick={() => go('/profile/feed')}
              testId="profile-nav-feed-preferences"
            />
          </div>

          <div className="h-px bg-slate-100 my-3" />

          {/* secondary, quieter */}
          <div className="flex flex-col gap-0.5">
            <ProfileNavRow
              icon={Settings}
              label="Account details"
              tone="secondary"
              onClick={() => go('/profile/account')}
              testId="profile-nav-account"
            />
            <ProfileNavRow
              icon={Brain}
              label="What MAHI remembers"
              tone="secondary"
              onClick={() => go('/profile/memory')}
              testId="profile-nav-memory"
            />
            <ProfileNavRow
              icon={SlidersHorizontal}
              label="Personality & notifications"
              tone="secondary"
              onClick={() => go('/profile/preferences')}
              testId="profile-nav-preferences"
            />
            {/* Only offered where it can actually run — the tour points at the
                card, the try-on button and the orb, none of which exist
                anywhere but Discover. The panel closes first so she has an
                unobstructed screen to move across. */}
            {onReplayTour && (
              <ProfileNavRow
                icon={Compass}
                label="Show me around again"
                tone="secondary"
                onClick={() => { onClose?.(); setTimeout(onReplayTour, 320); }}
                testId="profile-nav-replay-tour"
              />
            )}
            <ProfileNavRow
              icon={MessageSquareHeart}
              label="Something wrong? Tell us"
              tone="secondary"
              onClick={() => go('/feedback', { from: location.pathname })}
              testId="profile-nav-feedback"
            />
            <ProfileNavRow
              icon={Gift}
              label="Invite a friend"
              sublabel="Extra try-ons for both of you"
              tone="secondary"
              onClick={() => go('/invite')}
              testId="profile-nav-invite"
            />
            <ProfileNavRow
              icon={ShieldCheck}
              label="Privacy"
              tone="secondary"
              onClick={() => go('/profile/privacy')}
              testId="profile-nav-privacy"
            />
            <ProfileNavRow
              icon={LogOut}
              label="Sign out"
              tone="secondary"
              onClick={signOut}
              testId="profile-nav-signout"
            />
            <div className="flex items-center justify-center gap-1.5 pt-2 pb-1 text-[11px] text-slate-400 font-mahi-sans">
              <button type="button" onClick={() => go('/terms')} data-testid="profile-nav-terms"
                className="hover:text-slate-600 underline underline-offset-2">Terms</button>
              <span>·</span>
              <button type="button" onClick={() => go('/privacy-policy')} data-testid="profile-nav-privacy-policy"
                className="hover:text-slate-600 underline underline-offset-2">Privacy Policy</button>
            </div>
          </div>
        </div>

        <div className="shrink-0 px-5 pb-5 pt-2 flex items-center justify-center gap-2 text-slate-300">
          <MahiRobot size={16} />
          <span className="text-[11px] font-mahi-sans">MAHI · v{APP_VERSION}</span>
        </div>
      </motion.div>
    </>
  );
}
